// app/frontend/pages/Auth/Register.tsx
import { useState } from "react";
import { useForm } from "@mantine/form";
import { Stack, Anchor } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { L2KH1, L2KH2, L2KText, L2KCaption } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import {
  L2KInput,
  L2KPasswordInput,
  L2KButton,
} from "../../components/ui/Forms";
import AuthFormLayout from "../../components/layouts/AuthFormLayout";
import { SEO } from "../../components/common/SEO";

export default function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name is too short" : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      password: (value) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
      password_confirmation: (value, values) =>
        value !== values.password ? "Passwords do not match" : null,
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const formData = new FormData();
      formData.append("user[name]", values.name);
      formData.append("user[email]", values.email);
      formData.append("user[password]", values.password);
      formData.append(
        "user[password_confirmation]",
        values.password_confirmation
      );

      const response = await fetch("/", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        setRegisteredEmail(values.email);
        setRegistrationComplete(true);
        notifications.show({
          color: "green",
          title: "Account Created!",
          message: "Check your email to confirm your account.",
          icon: <IconCheck />,
          autoClose: 5000,
        });
      } else {
        const data = await response.json().catch(() => ({}));
        const errors = data.errors || {};

        notifications.show({
          color: "red",
          title: "Registration Failed",
          message:
            Object.values(errors).flat().join(", ") ||
            "Please check your information",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });

        // Map Rails errors to form fields
        const formErrors: Record<string, string> = {};
        if (errors.email) formErrors.email = errors.email.join(", ");
        if (errors.password) formErrors.password = errors.password.join(", ");
        if (errors.name) formErrors.name = errors.name.join(", ");
        form.setErrors(formErrors);
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred during registration",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <AuthFormLayout>
        <SEO title="Confirm Your Email — Learntokiz" />
        <Stack gap={isMobile ? "md" : "lg"}>
          <Stack gap={isMobile ? 4 : "xs"} ta="center">
            {isMobile ? (
              <L2KH2 variant="accent">Check Your Email</L2KH2>
            ) : (
              <L2KH1 variant="accent">Check Your Email</L2KH1>
            )}
            {isMobile ? (
              <L2KCaption variant="secondary">
                We've sent a confirmation link to{" "}
                <strong style={{ color: "#FF9800" }}>{registeredEmail}</strong>
              </L2KCaption>
            ) : (
              <L2KText variant="secondary">
                We've sent a confirmation link to{" "}
                <strong style={{ color: "#FF9800" }}>{registeredEmail}</strong>
              </L2KText>
            )}
          </Stack>

          <L2KCard variant="elevated" p={isMobile ? "md" : "xl"}>
            <Stack gap={isMobile ? "sm" : "md"} ta="center">
              {isMobile ? (
                <L2KCaption variant="secondary">
                  Click the link in the email to activate your account, then log
                  in to complete your profile.
                </L2KCaption>
              ) : (
                <L2KText variant="secondary">
                  Click the link in the email to activate your account, then log
                  in to complete your profile.
                </L2KText>
              )}
              <L2KCaption variant="tertiary">
                Didn't receive the email? Check your spam folder or{" "}
                <Anchor
                  href="/confirmation/new"
                  size="xs"
                  style={{ color: "#FF9800" }}
                >
                  resend confirmation
                </Anchor>
              </L2KCaption>
              <Anchor href="/login" size={isMobile ? "xs" : "sm"} style={{ color: "#FF9800" }}>
                Back to Sign In
              </Anchor>
            </Stack>
          </L2KCard>
        </Stack>
      </AuthFormLayout>
    );
  }

  return (
    <AuthFormLayout>
      <SEO title="Create Account - Learntokiz" />
      <Stack gap={isMobile ? "md" : "lg"}>
        <Stack gap={isMobile ? 4 : "xs"} ta="center">
          {isMobile ? (
            <L2KH2 variant="accent">Create Account</L2KH2>
          ) : (
            <L2KH1 variant="accent">Create Account</L2KH1>
          )}
          {isMobile ? (
            <L2KCaption variant="secondary">
              Already have an account?{" "}
              <Anchor href="/login" size="xs" style={{ color: "#FF9800" }}>
                Sign in
              </Anchor>
            </L2KCaption>
          ) : (
            <L2KText variant="secondary">
              Already have an account?{" "}
              <Anchor href="/login" style={{ color: "#FF9800" }}>
                Sign in
              </Anchor>
            </L2KText>
          )}
        </Stack>

        <L2KCard variant="elevated" glow p={isMobile ? "md" : "xl"}>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap={isMobile ? "sm" : "md"}>
              <L2KInput
                label="Name"
                placeholder="Your name"
                required
                size={isMobile ? "sm" : "md"}
                {...form.getInputProps("name")}
              />
              <L2KInput
                label="Email"
                placeholder="you@example.com"
                required
                size={isMobile ? "sm" : "md"}
                {...form.getInputProps("email")}
              />
              <L2KPasswordInput
                label="Password"
                placeholder="At least 6 characters"
                required
                size={isMobile ? "sm" : "md"}
                {...form.getInputProps("password")}
              />
              <L2KPasswordInput
                label="Confirm Password"
                placeholder="Confirm your password"
                required
                size={isMobile ? "sm" : "md"}
                {...form.getInputProps("password_confirmation")}
              />
              <L2KButton
                fullWidth
                mt={isMobile ? "xs" : "md"}
                size={isMobile ? "sm" : "md"}
                type="submit"
                loading={isLoading}
              >
                Create Account
              </L2KButton>
            </Stack>
          </form>
        </L2KCard>

        <L2KCaption variant="tertiary" ta="center">
          By creating an account, you agree to our Terms of Service and Privacy
          Policy.
        </L2KCaption>
      </Stack>
    </AuthFormLayout>
  );
}
